/**
 * Sprint 23.7 — `<RailItem>`, the single building block of the reworked left
 * rail (Home, Workforce, Tables, Library, Connections, My tasks, Activity,
 * Settings). One component, every nav row — per the visual-spec-checklist's
 * "name shared components, never re-spec CSS" rule (check 5).
 *
 * Default blends with the rail; active + hover both lift to the secondary
 * background with primary text (active additionally goes 500-weight). The
 * horizontal margin gives the active state its inset-pill look.
 */

import { useState, type CSSProperties, type ReactNode } from "react";

export type RailCountVariant = "tertiary" | "info-pill";

export interface RailItemProps {
  /** Tabler icon name without the `ti-` prefix (e.g. "home", "users"). */
  icon: string;
  label: string;
  /** Trailing count — rendered only when > 0. */
  count?: number;
  /** "tertiary" (quiet) or "info-pill" (attention, e.g. My tasks). */
  countVariant?: RailCountVariant;
  active?: boolean;
  onClick?: () => void;
  /** Icon font-size; 16 by default, 15 for Settings to match visually. */
  iconSize?: number;
  /** Optional override for the trailing slot (e.g. a custom badge). */
  trailing?: ReactNode;
  title?: string;
  "aria-label"?: string;
}

export function RailItem({
  icon,
  label,
  count,
  countVariant = "tertiary",
  active = false,
  onClick,
  iconSize = 16,
  trailing,
  title,
  "aria-label": ariaLabel,
}: RailItemProps) {
  const [hover, setHover] = useState(false);
  const lifted = active || hover;

  const style: CSSProperties = {
    padding: "6px 10px",
    margin: "0 8px",
    borderRadius: "var(--border-radius-md)",
    display: "flex",
    alignItems: "center",
    gap: 10,
    width: "calc(100% - 16px)",
    border: "none",
    cursor: "pointer",
    fontFamily: "var(--font-sans)",
    fontSize: 13,
    textAlign: "left",
    background: lifted ? "var(--color-background-secondary)" : "transparent",
    color: lifted ? "var(--color-text-primary)" : "var(--color-text-secondary)",
    fontWeight: active ? 500 : 400,
    transition: "background 120ms ease, color 120ms ease",
  };

  const showCount = typeof count === "number" && count > 0;

  return (
    <button
      type="button"
      onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      aria-current={active ? "page" : undefined}
      aria-label={ariaLabel}
      title={title}
      style={style}
    >
      <i
        className={`ti ti-${icon}`}
        style={{ fontSize: iconSize, flex: "0 0 auto", color: "inherit" }}
        aria-hidden="true"
      />
      <span
        style={{
          flex: 1,
          minWidth: 0,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {label}
      </span>
      {trailing ??
        (showCount ? (
          countVariant === "info-pill" ? (
            <span style={infoPillStyle}>{count}</span>
          ) : (
            <span style={tertiaryCountStyle}>{count}</span>
          )
        ) : null)}
    </button>
  );
}

const tertiaryCountStyle: CSSProperties = {
  flex: "0 0 auto",
  fontSize: 11,
  color: "var(--color-text-tertiary)",
  fontWeight: 400,
  fontVariantNumeric: "tabular-nums",
};

const infoPillStyle: CSSProperties = {
  flex: "0 0 auto",
  fontSize: 11,
  padding: "1px 6px",
  borderRadius: 999,
  background: "var(--color-background-info)",
  color: "var(--color-text-info)",
  fontWeight: 500,
  fontVariantNumeric: "tabular-nums",
};
